import {
    Heading1,
    Heading2,
    Heading3,
    AlignLeft,
    Image as ImageBlockIcon,
    List,
    Quote,
    Plus,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BlogBlockRenderer, type BlogBlock } from '../blocks';

interface BlockEditorApi {
    blocks: BlogBlock[];
    addBlock: (type: BlogBlock['type'], options?: { overrides?: Partial<BlogBlock> }) => void;
    updateBlock: (id: string, updates: Partial<BlogBlock>) => void;
    moveBlock: (id: string, direction: 'up' | 'down') => void;
    removeBlock: (id: string) => void;
    duplicateBlock: (id: string) => void;
}

export function BlogBlocksPanel({
    blockEditor,
    onImagePick,
    resolveImageUrl,
}: {
    blockEditor: BlockEditorApi;
    onImagePick: (blockId: string) => void;
    resolveImageUrl: (url: string) => string;
}) {
    const sorted = [...blockEditor.blocks].sort((a, b) => a.order - b.order);

    return (
        <div className="space-y-2">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-muted"
                    >
                        <Plus size={14} />
                        Añadir bloque
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem
                        onClick={() => blockEditor.addBlock('heading', { overrides: { level: 1 } as Partial<BlogBlock> })}
                    >
                        <Heading1 className="w-4 h-4 mr-2" />
                        Título 1
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => blockEditor.addBlock('heading')}>
                        <Heading2 className="w-4 h-4 mr-2" />
                        Título 2
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => blockEditor.addBlock('heading', { overrides: { level: 3 } as Partial<BlogBlock> })}
                    >
                        <Heading3 className="w-4 h-4 mr-2" />
                        Título 3
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => blockEditor.addBlock('paragraph')}>
                        <AlignLeft className="w-4 h-4 mr-2" />
                        Párrafo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => blockEditor.addBlock('image')}>
                        <ImageBlockIcon className="w-4 h-4 mr-2" />
                        Imagen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => blockEditor.addBlock('list')}>
                        <List className="w-4 h-4 mr-2" />
                        Lista
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => blockEditor.addBlock('blockquote')}>
                        <Quote className="w-4 h-4 mr-2" />
                        Cita
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <div className="rounded-lg border border-border bg-background min-h-[200px] p-4 space-y-2">
                {sorted.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                        Sin bloques. Mejor usa HTML si el artículo tiene tablas o FAQ.
                    </p>
                ) : (
                    sorted.map((block, idx) => (
                        <BlogBlockRenderer
                            key={block.id}
                            block={block}
                            isFirst={idx === 0}
                            isLast={idx === sorted.length - 1}
                            onUpdate={(u) => blockEditor.updateBlock(block.id, u)}
                            onMoveUp={() => blockEditor.moveBlock(block.id, 'up')}
                            onMoveDown={() => blockEditor.moveBlock(block.id, 'down')}
                            onRemove={() => blockEditor.removeBlock(block.id)}
                            onDuplicate={() => blockEditor.duplicateBlock(block.id)}
                            onImagePick={onImagePick}
                            resolveImageUrl={resolveImageUrl}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
